import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  Firestore,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  DocumentReference,
} from '@angular/fire/firestore';
import { User as FirebaseAuthUser } from '@angular/fire/auth';
import { User } from '../../models/user.model';
import { LoggerService } from '../common/logger.service';

@Injectable({ providedIn: 'root' })
export class UserOnboardingService {
  constructor(
    private functions: Functions,
    private firestore: Firestore,
    private logger: LoggerService,
  ) {}

  // Generate a random username using the cloud function
  private async generateRandomUsername(): Promise<string> {
    this.logger.info('[Onboarding] Generating random username...');
    const generateUsernameFn = httpsCallable<
      any,
      { username: string; error?: string }
    >(this.functions, 'generateRandomUsername');
    try {
      const result = await generateUsernameFn();
      if (result?.data?.username) {
        this.logger.info(
          `[Onboarding] Username generated: ${result.data.username}`,
        );
        return result.data.username;
      }
      this.logger.warn(
        '[Onboarding] Username generation failed, using fallback.',
      );
      return this.generateFallbackUsername();
    } catch (error) {
      this.logger.error('[Onboarding] Failed to generate username:', error);
      return this.generateFallbackUsername();
    }
  }

  // Generate a fallback username locally
  private generateFallbackUsername(): string {
    const fallback = `user_${Math.floor(Math.random() * 10000)}`;
    this.logger.info(`[Onboarding] Fallback username generated: ${fallback}`);
    return fallback;
  }

  // Generate a random profile image using the cloud function
  private async generateRandomProfileImage(): Promise<string | null> {
    this.logger.info('[Onboarding] Generating random profile image...');
    const generateImageFn = httpsCallable<
      any,
      { imageUrl: string | null; error?: string }
    >(this.functions, 'generateRandomProfileImage');
    try {
      const result = await generateImageFn();
      if (result?.data?.imageUrl) {
        this.logger.info(
          `[Onboarding] Profile image generated: ${result.data.imageUrl}`,
        );
        return result.data.imageUrl;
      }
      this.logger.warn('[Onboarding] Profile image generation failed.');
      return null;
    } catch (error) {
      this.logger.error(
        '[Onboarding] Failed to generate profile image:',
        error,
      );
      return null;
    }
  }

  // First-time user onboarding
  async onboardUser(firebaseUser: FirebaseAuthUser): Promise<void> {
    this.logger.info(
      `[Onboarding] Starting first-time onboarding for UID: ${firebaseUser.uid}`,
    );
    const userRef = doc(
      this.firestore,
      `users/${firebaseUser.uid}`,
    ) as DocumentReference<User>;

    // Generate initial user data
    const displayName = await this.generateRandomUsername();
    let photoURL = await this.generateRandomProfileImage();

    // Use firebaseUser.photoURL as fallback if generateRandomProfileImage returns null
    if (photoURL === null) {
      photoURL = firebaseUser.photoURL;
      this.logger.info(
        `[Onboarding] Using firebaseUser.photoURL as fallback: ${photoURL}`,
      );
    }

    this.logger.info(
      `[Onboarding] ${firebaseUser.uid} user's username: ${displayName}`,
    );
    this.logger.info(
      `[Onboarding] ${firebaseUser.uid} user's profile image: ${photoURL}`,
    );

    const userData: User = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: displayName,
      photoURL: photoURL,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    };

    await setDoc(userRef, userData);
    this.logger.info(
      `[Onboarding] New user document created for UID: ${firebaseUser.uid}`,
    );
  }
}
